import { useEffect, useRef, useState } from 'react';
import { FormModal } from './FormModal';
import { buildTechnicalMetadata, submitBetaFeedbackForm } from './betaFeedback';
import {agentContentSettings} from '../content';

type NameSuggestionValues = {
  suggestedAgentName: string;
  name: string;
  email: string;
  reason: string;
  website: string;
};

type NameSuggestionModalProps = {
  open: boolean;
  endpoint: string;
  getThreadId: () => string | undefined;
  onClose: () => void;
};

const EMPTY_VALUES: NameSuggestionValues = {
  suggestedAgentName: '',
  name: '',
  email: '',
  reason: '',
  website: '',
};

function validate(values: NameSuggestionValues) {
  const copy = agentContentSettings.feedbackForms.nameSuggestion.validation;
  const errors: Partial<Record<keyof NameSuggestionValues, string>> = {};
  if (!values.suggestedAgentName.trim()) errors.suggestedAgentName = copy.suggestedNameRequired;
  if (!values.name.trim()) errors.name = copy.nameRequired;
  if (!values.email.trim()) errors.email = copy.emailRequired;
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) errors.email = copy.emailInvalid;
  if (!values.reason.trim()) errors.reason = copy.reasonRequired;
  if (
    values.suggestedAgentName.length > 80 || values.name.length > 100 || values.email.length > 254 || values.reason.length > 1500
  ) {
    const invalidField = values.suggestedAgentName.length > 80
      ? 'suggestedAgentName'
      : values.name.length > 100
        ? 'name'
        : values.email.length > 254
          ? 'email'
          : 'reason';
    errors[invalidField] = copy.tooLong;
  }
  return errors;
}

export function NameSuggestionModal({ open, endpoint, getThreadId, onClose }: NameSuggestionModalProps) {
  const copy = agentContentSettings.feedbackForms.nameSuggestion;
  const [values, setValues] = useState<NameSuggestionValues>(EMPTY_VALUES);
  const [errors, setErrors] = useState<Partial<Record<keyof NameSuggestionValues, string>>>({});
  const [attempted, setAttempted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState('');
  const [success, setSuccess] = useState(false);
  const inputRefs = useRef<Partial<Record<keyof NameSuggestionValues, HTMLInputElement | HTMLTextAreaElement | null>>>({});
  const submissionLock = useRef(false);

  const reset = () => {
    setValues(EMPTY_VALUES);
    setErrors({});
    setAttempted(false);
    setSubmitting(false);
    setSubmissionError('');
    setSuccess(false);
    submissionLock.current = false;
  };

  const handleCancel = () => {
    reset();
    onClose();
  };

  useEffect(() => {
    if (!open) reset();
  }, [open]);

  const updateValue = (field: keyof NameSuggestionValues, value: string) => {
    const nextValues = { ...values, [field]: value };
    setValues(nextValues);
    if (attempted || errors[field]) {
      const nextErrors = validate(nextValues);
      setErrors(current => ({ ...current, [field]: nextErrors[field] }));
    }
  };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting || submissionLock.current) return;

    const trimmed: NameSuggestionValues = {
      suggestedAgentName: values.suggestedAgentName.trim(),
      name: values.name.trim(),
      email: values.email.trim(),
      reason: values.reason.trim(),
      website: values.website,
    };
    const nextErrors = validate(trimmed);
    setAttempted(true);
    setErrors(nextErrors);
    setSubmissionError('');

    const firstInvalid = (['suggestedAgentName', 'name', 'email', 'reason'] as const).find(field => nextErrors[field]);
    if (firstInvalid) {
      requestAnimationFrame(() => inputRefs.current[firstInvalid]?.focus());
      return;
    }
    if (trimmed.website) return;

    setValues(trimmed);
    submissionLock.current = true;
    setSubmitting(true);
    try {
      await submitBetaFeedbackForm({
        endpoint,
        values: {
          suggestedAgentName: trimmed.suggestedAgentName,
          name: trimmed.name,
          email: trimmed.email,
          reason: trimmed.reason,
        },
        metadata: buildTechnicalMetadata({
          submissionType: 'NameSuggestion',
          threadId: getThreadId(),
          agentName: agentContentSettings.defaultAgentName,
        }),
      });
      setValues(EMPTY_VALUES);
      setErrors({});
      setSuccess(true);
    } catch {
      setSubmissionError(copy.validation.submitFailed);
    } finally {
      submissionLock.current = false;
      setSubmitting(false);
    }
  };

  return (
    <FormModal
      open={open}
      id="name-suggestion"
      title={copy.title}
      intro={copy.intro}
    >
      {success ? (
        <div className="account-form__confirmation-container" role="status">
          <p className="account-font__body-s twe-asa-form-success-message">{copy.success}</p>
          <div className="account-form__cta-container--centered">
            <button type="button" className="cta-button cta-button--delta" data-form-autofocus onClick={handleCancel}>{copy.cancelButtonText}</button>
          </div>
        </div>
      ) : (
        <form className="account-form__form" noValidate onSubmit={onSubmit}>
          <ul className="tweForm-content">
            <li className="tweForm-item form-inputs__container form-inputs__container--flex-column">
              <label className="tweForm-label form-inputs__label form-inputs__label--text-input" htmlFor="twe-asa-suggested-name">{copy.suggestedNameLabel}</label>
              <input
                ref={element => { inputRefs.current.suggestedAgentName = element; }}
                id="twe-asa-suggested-name"
                className={`tweForm-input form-inputs__text-input${errors.suggestedAgentName ? ' form-inputs__text-input--error' : ''}`}
                name="suggestedAgentName"
                type="text"
                required
                maxLength={80}
                autoComplete="off"
                value={values.suggestedAgentName}
                onChange={event => updateValue('suggestedAgentName', event.target.value)}
                aria-invalid={Boolean(errors.suggestedAgentName)}
                aria-describedby={errors.suggestedAgentName ? 'twe-asa-suggested-name-error' : undefined}
                data-form-autofocus
              />
              {errors.suggestedAgentName && <span id="twe-asa-suggested-name-error" className="tweForm-required form-inputs__label form-inputs__label--text-input form-inputs__label--error" role="alert">{errors.suggestedAgentName}</span>}
            </li>
            <li className="tweForm-item form-inputs__container form-inputs__container--flex-column">
              <label className="tweForm-label form-inputs__label form-inputs__label--text-input" htmlFor="twe-asa-name-suggestion-name">{copy.nameLabel}</label>
              <input
                ref={element => { inputRefs.current.name = element; }}
                id="twe-asa-name-suggestion-name"
                className={`tweForm-input form-inputs__text-input${errors.name ? ' form-inputs__text-input--error' : ''}`}
                name="name"
                type="text"
                required
                maxLength={100}
                autoComplete="name"
                value={values.name}
                onChange={event => updateValue('name', event.target.value)}
                aria-invalid={Boolean(errors.name)}
                aria-describedby={errors.name ? 'twe-asa-name-suggestion-name-error' : undefined}
              />
              {errors.name && <span id="twe-asa-name-suggestion-name-error" className="tweForm-required form-inputs__label form-inputs__label--text-input form-inputs__label--error" role="alert">{errors.name}</span>}
            </li>
            <li className="tweForm-item form-inputs__container form-inputs__container--flex-column">
              <label className="tweForm-label form-inputs__label form-inputs__label--text-input" htmlFor="twe-asa-name-suggestion-email">{copy.emailLabel}</label>
              <input
                ref={element => { inputRefs.current.email = element; }}
                id="twe-asa-name-suggestion-email"
                className={`tweForm-input form-inputs__text-input${errors.email ? ' form-inputs__text-input--error' : ''}`}
                name="email"
                type="email"
                required
                maxLength={254}
                autoComplete="email"
                value={values.email}
                onChange={event => updateValue('email', event.target.value)}
                aria-invalid={Boolean(errors.email)}
                aria-describedby={errors.email ? 'twe-asa-name-suggestion-email-error' : undefined}
              />
              {errors.email && <span id="twe-asa-name-suggestion-email-error" className="tweForm-required form-inputs__label form-inputs__label--text-input form-inputs__label--error" role="alert">{errors.email}</span>}
            </li>
            <li className="tweForm-item form-inputs__container form-inputs__container--flex-column">
              <label className="tweForm-label form-inputs__label form-inputs__label--text-area" htmlFor="twe-asa-name-reason">{copy.reasonLabel}</label>
              <textarea
                ref={element => { inputRefs.current.reason = element; }}
                id="twe-asa-name-reason"
                className={`tweForm-input form-inputs__text-area${errors.reason ? ' form-inputs__text-input--error' : ''}`}
                name="reason"
                required
                maxLength={1500}
                rows={5}
                value={values.reason}
                onChange={event => updateValue('reason', event.target.value)}
                aria-invalid={Boolean(errors.reason)}
                aria-describedby={`${errors.reason ? 'twe-asa-name-reason-error ' : ''}twe-asa-name-reason-counter`.trim()}
              />
              {errors.reason && <span id="twe-asa-name-reason-error" className="tweForm-required form-inputs__label form-inputs__label--text-input form-inputs__label--error" role="alert">{errors.reason}</span>}
              <p id="twe-asa-name-reason-counter" className="account-font__body-xs" aria-live="polite">{values.reason.length} / 1500</p>
            </li>
          </ul>
          <div className="twe-asa-form-honeypot" aria-hidden="true">
            <label htmlFor="twe-asa-name-website">{copy.websiteLabel}</label>
            <input
              ref={element => { inputRefs.current.website = element; }}
              id="twe-asa-name-website"
              name="website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={values.website}
              onChange={event => updateValue('website', event.target.value)}
            />
          </div>
          <div className="tweForm-item form-inputs__container form-inputs__container--flex-column account-form__confirmation-container">
            <p className="account-font__body-xs">{copy.privacyNotice}</p>
            <p className="account-font__body-xs">{copy.privacyPrefix} <a href={copy.privacyLinkUrl} target="_blank" rel="noopener noreferrer">{copy.privacyLinkText}</a>.</p>
          </div>
          {submissionError && <span className="tweForm-required form-inputs__label form-inputs__label--text-input form-inputs__label--error" role="alert">{submissionError}</span>}
          <div className="account-form__cta-container--centered">
            <button type="submit" className={`cta-button cta-button--alpha${submitting ? ' cta-button--disabled' : ''}`} disabled={submitting}>{submitting ? copy.submittingButtonText : copy.submitButtonText}</button>{' '}
            <button type="button" className="cta-button cta-button--delta" onClick={handleCancel}>{copy.cancelButtonText}</button>
          </div>
        </form>
      )}
    </FormModal>
  );
}
